import { useQuery } from "@tanstack/react-query";

import { api } from "@/api/client";
import { useMe } from "@/api/me";

/** Either-or window selector for the cost views. ``days`` rolls a
 *  preset (last 7/30/…); ``range`` is the custom local-date pair the
 *  picker emits, already converted to UTC ISO datetimes by the SPA so
 *  the backend stays timezone-agnostic. */
export type CostWindow =
  | { kind: "days"; days: number }
  | { kind: "range"; start: string; end: string };

function costWindowQuery(w: CostWindow): string {
  if (w.kind === "days") return `days=${encodeURIComponent(w.days)}`;
  return (
    `start=${encodeURIComponent(w.start)}` +
    `&end=${encodeURIComponent(w.end)}`
  );
}

export type AdminCostsSummary = {
  window_days: number;
  window_start?: string;
  window_end?: string;
  total_tokens: number;
  total_prompt_tokens: number;
  total_cached_prompt_tokens: number;
  total_completion_tokens: number;
  total_reasoning_tokens: number;
  total_cost_usd_micros: number;
  daily: Array<{
    day: string;
    tokens: number;
    prompt_tokens: number;
    cached_prompt_tokens: number;
    completion_tokens: number;
    reasoning_tokens: number;
    cost_usd_micros: number;
  }>;
  leaderboard: Array<{
    user_id: string;
    email: string;
    display_name: string;
    tokens: number;
    prompt_tokens: number;
    cached_prompt_tokens: number;
    completion_tokens: number;
    reasoning_tokens: number;
    cost_usd_micros: number;
  }>;
};

export function useAdminCostsSummary(
  window: CostWindow = { kind: "days", days: 30 },
  unit: "cost" | "credits" = "cost",
) {
  const query = costWindowQuery(window);
  // Rapid org-switching reuses the same window/unit, so the active org
  // slug goes into the queryKey too — otherwise switching tenants could
  // serve a stale cross-org cache entry before the refetch lands.
  const activeOrgSlug = useMe().data?.admin_scope?.active_org_slug ?? null;
  return useQuery({
    // The window object goes into the queryKey so two different
    // pickers don't collapse onto the same cache entry — switching
    // from "Last 7 days" to "Last 30" or to a custom range each
    // gets its own cache slot. ``unit`` + ``activeOrgSlug`` keep the
    // $/credits view and per-org scope on separate slots.
    queryKey: ["admin", "costs", "summary", window, unit, activeOrgSlug],
    queryFn: async () =>
      (
        await api.get<AdminCostsSummary>(
          `/admin/costs/summary?${query}&unit=${unit}`,
        )
      ).data,
    staleTime: 60_000,
  });
}

export type AdminCostsUserSession = {
  session_id: string;
  title: string | null;
  intent: string | null;
  provider: string | null;
  turn_count: number;
  first_turn_at: string | null;
  last_turn_at: string | null;
  tokens: number;
  prompt_tokens: number;
  cached_prompt_tokens: number;
  completion_tokens: number;
  reasoning_tokens: number;
  cost_usd_micros: number;
};

export type AdminCostsUserSessions = {
  user: { id: string; email: string; display_name: string };
  window_days: number;
  window_start?: string;
  window_end?: string;
  sessions: AdminCostsUserSession[];
};

/** Fetched on demand when a leaderboard row is expanded — kept off
 *  the always-loaded summary query so the costs tab doesn't pay for
 *  N-user drill-down data nobody clicked into. The window passed
 *  here is the same one the leaderboard is showing so the drill-down
 *  stays in sync with the parent view. */
export function useAdminCostsUserSessions(
  userId: string | null,
  window: CostWindow = { kind: "days", days: 30 },
  unit: "cost" | "credits" = "cost",
) {
  const query = costWindowQuery(window);
  const activeOrgSlug = useMe().data?.admin_scope?.active_org_slug ?? null;
  return useQuery({
    queryKey: ["admin", "costs", "user", userId, window, unit, activeOrgSlug],
    queryFn: async () =>
      (
        await api.get<AdminCostsUserSessions>(
          `/admin/costs/users/${userId}/sessions?${query}&unit=${unit}`,
        )
      ).data,
    enabled: Boolean(userId),
    staleTime: 30_000,
  });
}

export type AdminCostsTurn = {
  turn_id: string;
  created_at: string;
  model: string | null;
  provider: string | null;
  account_id: string | null;
  account_kind: string | null;
  account_label: string | null;
  tokens: number;
  prompt_tokens: number;
  cached_prompt_tokens: number;
  completion_tokens: number;
  reasoning_tokens: number;
  cost_usd_micros: number;
  prompt_text: string;
  // Credits-mode drill-down only: how the credit debit was derived.
  multiplier_kind?: string | null;
  multiplier_bp?: number | null;
  raw_cost_micros?: number | null;
};

export type AdminCostsSessionTurns = {
  session: {
    id: string;
    title: string | null;
    user_id: string;
    intent: string | null;
    provider: string | null;
  };
  turns: AdminCostsTurn[];
};

/** Drill-down step 2: every turn for one session with prompt text +
 *  auth attribution. Fired only when the user expands a session row
 *  inside the leaderboard drawer. */
export function useAdminCostsSessionTurns(
  sessionId: string | null,
  unit: "cost" | "credits" = "cost",
) {
  const activeOrgSlug = useMe().data?.admin_scope?.active_org_slug ?? null;
  return useQuery({
    queryKey: ["admin", "costs", "session", sessionId, unit, activeOrgSlug],
    queryFn: async () =>
      (
        await api.get<AdminCostsSessionTurns>(
          `/admin/costs/sessions/${sessionId}/turns?unit=${unit}`,
        )
      ).data,
    enabled: Boolean(sessionId),
    staleTime: 30_000,
  });
}
